import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { PortalDrawService } from '@tsin-core/services/http/portal-draw.service';
import { catchError, tap } from 'rxjs';
import { DeletePortalDrawById, GetActivatedPortalDraws, GetAllPortalDraws, GetArchivedPortalDraws, GetSinglePortalDrawByExamId, GetSinglePortalDrawById, GetUndrawnApplications, InitializeManualPortalDraw, InitializePortalDraw, SetSelectedDraw, UnarchivePortalDrawById, UpdatePortalDraw } from './portal-draw.actions';

export class PortalDrawStateModel {
  public portalDraws: any[] = [];
  public activatedDraws: any[] = [];
  public archivedDraws: any[] = [];
  public selectedPortalDraw: any;
  public loading!: LoadingState;
  public totalApplications: number = 0;
  /** Submitted applications belonging to no draw, oldest submission first.
   *  Candidatures soumises sans tirage, de la plus ancienne à la plus récente. */
  public undrawnApplications: any[] = [];
  public totalUndrawnApplications: number = 0;
  public undrawnLoading!: LoadingState;
}

const defaults = {
  portalDraws: [],
  activatedDraws: [],
  archivedDraws: [],
  totalApplications: 0,
  selectedPortalDraw: null,
  loading: LoadingState.notLoading,
  undrawnApplications: [],
  totalUndrawnApplications: 0,
  undrawnLoading: LoadingState.notLoading,
};

@State<PortalDrawStateModel>({
  name: 'portalDrawState',
  defaults,
})
@Injectable()
export class PortalDrawState {
  portalDrawService: PortalDrawService = inject(PortalDrawService);

  @Selector()
  static getPortalDraws(state: PortalDrawStateModel) {
    return state.portalDraws;
  }

  @Selector()
  static getActivatedDraws(state: PortalDrawStateModel) {
    return state.activatedDraws;
  }

  @Selector()
  static getSelectedPortalDraw(state: PortalDrawStateModel) {
    return state.selectedPortalDraw;
  }

  @Selector()
  static getTotalApplications(state: PortalDrawStateModel) {
    return state.totalApplications;
  }

  @Selector()
  static getLoading(state: PortalDrawStateModel) {
    return state.loading;
  }

  @Selector()
  static getArchivedDraws(state: PortalDrawStateModel) {
    return state.archivedDraws;
  }

  @Selector()
  static getUndrawnApplications(state: PortalDrawStateModel) {
    return state.undrawnApplications;
  }

  @Selector()
  static getTotalUndrawnApplications(state: PortalDrawStateModel) {
    return state.totalUndrawnApplications;
  }

  @Selector()
  static getUndrawnLoading(state: PortalDrawStateModel) {
    return state.undrawnLoading;
  }

  @Action(GetAllPortalDraws)
  getAllPortalDraws({ patchState }: StateContext<PortalDrawStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getAllDraws().pipe(
      tap((portalDraws: any) => {
        patchState({ portalDraws, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetActivatedPortalDraws)
  getActivatedPortalDraws({ patchState }: StateContext<PortalDrawStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getActivatedDraws().pipe(
      tap((activatedDraws: any) => {
        patchState({ activatedDraws, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(InitializePortalDraw)
  initializePortalDraw({ getState, patchState }: StateContext<PortalDrawStateModel>, action: InitializePortalDraw) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.portalDrawService.initializeDraw(action.payload).pipe(
      tap((portalDraw: any) => {
        const portalDraws = getState().portalDraws;
        patchState({ portalDraws: [...portalDraws, portalDraw], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(InitializeManualPortalDraw)
  initializeManualPortalDraw({ getState, patchState }: StateContext<PortalDrawStateModel>, action: InitializeManualPortalDraw) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.portalDrawService.initializeManualDraw(action.payload).pipe(
      tap((portalDraw: any) => {
        const portalDraws = getState().portalDraws;
        patchState({ portalDraws: [...portalDraws, portalDraw], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(UpdatePortalDraw)
  updatePortalDraw({ patchState }: StateContext<PortalDrawStateModel>, action: UpdatePortalDraw) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.updateDraw(action.payload).pipe(
      tap((portalDraw: any) => {
        patchState({ selectedPortalDraw: portalDraw, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetSinglePortalDrawByExamId)
  getSinglePortalDrawByExamId({ patchState }: StateContext<PortalDrawStateModel>, action: GetSinglePortalDrawByExamId) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getSingleDrawByExamId(action.payload).pipe(
      tap((portalDraw: any) => {
        patchState({ portalDraws: portalDraw.draws, totalApplications: portalDraw.totalApplications, loading: LoadingState.notLoading });
      }),
      // The API answers 404 for a cohort with no draws. Clear the list so the
      // previous cohort's draws (or a draw just created for another cohort) are
      // not left on screen.
      // 404 pour une cohorte sans tirage : on vide la liste.
      catchError((error) => {
        patchState({ portalDraws: [], totalApplications: 0, loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetSinglePortalDrawById)
  getSinglePortalDrawById({ patchState }: StateContext<PortalDrawStateModel>, action: GetSinglePortalDrawById) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getSingleDrawById(action.payload).pipe(
      tap((portalDraw: any) => {
        patchState({ selectedPortalDraw: portalDraw, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(DeletePortalDrawById)
  deletePortalDrawById({ patchState }: StateContext<PortalDrawStateModel>, action: DeletePortalDrawById) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.deleteDrawById(action.payload).pipe(
      tap(() => {
        patchState({ selectedPortalDraw: null, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SetSelectedDraw)
  setSelectedDraw({ patchState }: StateContext<PortalDrawStateModel>, action: SetSelectedDraw) {
    patchState({ selectedPortalDraw: action.payload });
  }

  @Action(GetUndrawnApplications)
  getUndrawnApplications({ patchState }: StateContext<PortalDrawStateModel>, action: GetUndrawnApplications) {
    patchState({ undrawnLoading: LoadingState.loadingList });
    return this.portalDrawService.getUndrawnApplications(action.payload).pipe(
      tap((response) => {
        patchState({
          undrawnApplications: response?.data ?? [],
          totalUndrawnApplications: response?.total ?? 0,
          undrawnLoading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        patchState({ undrawnApplications: [], totalUndrawnApplications: 0, undrawnLoading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetArchivedPortalDraws)
  getArchivedPortalDraws({ patchState }: StateContext<PortalDrawStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.portalDrawService.getArchivedDraws().pipe(
      tap((archivedDraws: any) => {
        patchState({ archivedDraws, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(UnarchivePortalDrawById)
  unarchivePortalDrawById({ getState, patchState }: StateContext<PortalDrawStateModel>, action: UnarchivePortalDrawById) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.portalDrawService.unarchiveDrawById(action.payload).pipe(
      tap(() => {
        patchState({
          archivedDraws: getState().archivedDraws.filter((draw) => draw?._id !== action.payload),
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }
}
